
import { WorldCountry } from './types';
import { analyzeDataIntegrity, cleanDuplicates } from '@/utils/dataAnalysisUtils';

/**
 * Función para limpiar slugs inválidos
 */
const cleanSlug = (slug: string): string => {
  return slug
    .toLowerCase()
    .normalize('NFD')
    .replace(/[\u0300-\u036f]/g, '') // Remover acentos
    .replace(/[üğışçöÜĞIŞÇÖ]/g, (match) => {
      const map: Record<string, string> = {
        'ü': 'u', 'ğ': 'g', 'ı': 'i', 'ş': 's', 'ç': 'c', 'ö': 'o',
        'Ü': 'u', 'Ğ': 'g', 'I': 'i', 'Ş': 's', 'Ç': 'c', 'Ö': 'o'
      };
      return map[match] || match;
    })
    .replace(/[^a-z0-9\s-]/g, '') // Remover caracteres especiales
    .replace(/\s+/g, '-') // Espacios a guiones
    .replace(/-+/g, '-') // Múltiples guiones a uno
    .replace(/^-|-$/g, ''); // Remover guiones al inicio/final
};

/**
 * Función para limpiar nombres de ciudades con números
 */
const cleanCityName = (name: string): string => {
  return name.replace(/\s+\d+$/, '').trim();
};

/**
 * Función principal de limpieza de datos
 */
export const cleanCountryData = (countries: WorldCountry[]): WorldCountry[] => {
  console.log('🧹 Iniciando limpieza completa de datos...');
  
  // Paso 1: Limpiar slugs inválidos y nombres de ciudades
  const cleanedCountries = countries.map(country => ({
    ...country,
    topCities: country.topCities.map(city => {
      const cleanedName = cleanCityName(city.name);
      const cleanedSlug = cleanSlug(cleanedName);
      
      return {
        ...city,
        name: cleanedName,
        slug: cleanedSlug
      };
    })
  }));

  // Paso 2: Eliminar duplicados usando la función existente
  const deduplicatedCountries = cleanDuplicates(cleanedCountries);
  
  // Paso 3: Eliminar ciudades duplicadas dentro de cada país
  const finalCountries = deduplicatedCountries.map(country => {
    const seenSlugs = new Set<string>();
    const uniqueCities = country.topCities.filter(city => {
      if (seenSlugs.has(city.slug)) {
        console.warn(`⚠️ Ciudad duplicada eliminada: ${city.name} (${city.slug}) en ${country.name}`);
        return false;
      }
      seenSlugs.add(city.slug);
      return true;
    });

    return {
      ...country,
      topCities: uniqueCities
    };
  });

  // Paso 4: Análisis final para verificar la limpieza
  const finalAnalysis = analyzeDataIntegrity(finalCountries);
  
  console.log('✅ LIMPIEZA COMPLETADA:');
  console.log(`   🌍 Países: ${finalAnalysis.summary.totalCountries}`);
  console.log(`   🏙️ Ciudades: ${finalAnalysis.summary.totalCities}`);
  console.log(`   ❌ Países duplicados restantes: ${finalAnalysis.summary.duplicateCountriesFound}`);
  console.log(`   ❌ Ciudades duplicadas restantes: ${finalAnalysis.summary.duplicateCitiesFound}`);
  console.log(`   ⚠️ Slugs inválidos restantes: ${finalAnalysis.summary.invalidSlugsFound}`);
  console.log(`   📭 Países vacíos restantes: ${finalAnalysis.summary.emptyCountriesFound}`);

  return finalCountries;
};

/**
 * Función para validar que la limpieza fue exitosa
 */
export const validateCleanup = (countries: WorldCountry[]): boolean => {
  const analysis = analyzeDataIntegrity(countries);
  
  const isClean = analysis.summary.duplicateCountriesFound === 0 &&
                  analysis.summary.duplicateCitiesFound === 0 &&
                  analysis.summary.invalidSlugsFound === 0;
  
  if (isClean) {
    console.log('🎉 DATOS COMPLETAMENTE LIMPIOS - Sin errores detectados');
  } else {
    console.error('❌ LIMPIEZA INCOMPLETA - Revisar errores restantes');
  }
  
  return isClean;
};
